import React, { useState, cloneElement, Component } from 'react';
import { motion } from 'framer-motion';
import { SparkleEffect } from '../components/SparkleEffect';
import { GlitterHeart } from '../components/GlitterHeart';
import { CalendarIcon, MessageCircleIcon, SparklesIcon, FlameIcon } from 'lucide-react';
export function CoachingPage() {
  const handleRequestSession = (tier: string) => {
    const subject = encodeURIComponent(`Coaching Request - ${tier}`);
    const body = encodeURIComponent(`Hi Mandy!\n\nI'm interested in the ${tier} coaching option.\n\nPlease reach out so we can discuss details and get started!\n\nThank you!`);
    window.location.href = `mailto:trippinwithmandy@gmail.com?subject=${subject}&body=${body}`;
  };
  return <div className="min-h-screen bg-white pt-24 pb-24" data-id="element-343">
      {/* Hero */}
      <section className="relative py-20 text-center overflow-hidden bg-gradient-to-b from-blush-50 to-white" data-id="element-344">
        <SparkleEffect count={80} data-id="element-345" />
        <div className="max-w-3xl mx-auto px-4 relative z-10" data-id="element-346">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="font-heading text-5xl md:text-6xl font-bold text-plum-700 mb-6" data-id="element-347">
            Trippin' Through <span className="text-cta-500" data-id="element-348">Healing</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-plum-500 font-medium" data-id="element-349">
            Real-life guidance, mindset support, and confidence rebuilding—not
            therapy, but practical help navigating real-life challenges.
          </motion.p>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-lg text-cta-500 font-bold mt-4" data-id="element-350">
            Book your free call today
          </motion.p>
        </div>
      </section>

      {/* What to expect */}
      <section className="py-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-351">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-352">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="relative" data-id="element-353">
            <div className="aspect-square rounded-[3rem] bg-lavender-100 p-8 flex items-center justify-center relative overflow-hidden border border-lavender-200" data-id="element-354">
              <SparkleEffect count={40} data-id="element-355" />
              <div className="text-center z-10" data-id="element-356">
                <FlameIcon className="w-16 h-16 text-cta-500 mx-auto mb-4" data-id="element-357" />
                <h3 className="font-heading text-3xl text-plum-700 font-bold" data-id="element-358">
                  Who It's For
                </h3>
                <ul className="mt-6 space-y-3 text-left" data-id="element-359">
                  <li className="flex items-center gap-2 text-plum-700 font-medium" data-id="element-360">
                    <SparklesIcon className="w-4 h-4 text-cta-500 shrink-0" data-id="element-361" />{' '}
                    You're stuck and you know it.
                  </li>
                  <li className="flex items-center gap-2 text-plum-700 font-medium" data-id="element-362">
                    <SparklesIcon className="w-4 h-4 text-cta-500 shrink-0" data-id="element-363" />{' '}
                    You're tired of just surviving.
                  </li>
                  <li className="flex items-center gap-2 text-plum-700 font-medium" data-id="element-364">
                    <SparklesIcon className="w-4 h-4 text-cta-500 shrink-0" data-id="element-365" />{' '}
                    You need someone who won't judge your mess.
                  </li>
                  <li className="flex items-center gap-2 text-plum-700 font-medium" data-id="element-366">
                    <SparklesIcon className="w-4 h-4 text-cta-500 shrink-0" data-id="element-367" />{' '}
                    You're ready to do the damn work.
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} data-id="element-368">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-plum-700 mb-6" data-id="element-369">
              What to Expect
            </h2>
            <ul className="space-y-6" data-id="element-370">
              {[{
              title: 'Real talk, not textbook advice',
              desc: "I don't do fluff. We're going to look at your life honestly and figure out exactly what needs to change."
            }, {
              title: 'Someone who actually gives a damn',
              desc: 'When you work with me, I am fully invested in your healing and your success.'
            }, {
              title: 'Accountability with love (and tough love)',
              desc: 'I will hold your hand, but I will also call you out on your excuses. Because you deserve better.'
            }, {
              title: 'Mindset shifts that stick',
              desc: "We aren't just putting a band-aid on it. We are rewiring how you think and operate."
            }].map((item, i) => <li key={i} className="flex gap-4" data-id="element-371">
                  <div className="mt-1 w-2 h-2 rounded-full bg-cta-500 shrink-0" data-id="element-372" />
                  <div data-id="element-373">
                    <h4 className="font-bold text-plum-700 text-lg" data-id="element-374">
                      {item.title}
                    </h4>
                    <p className="text-plum-500 font-medium" data-id="element-375">{item.desc}</p>
                  </div>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-blush-50 mt-12" data-id="element-376">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-377">
          <h2 className="font-heading text-4xl font-bold text-plum-700 mb-16" data-id="element-378">
            How It Works
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative" data-id="element-379">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-12 left-[15%] right-[15%] h-0.5 bg-blush-200" data-id="element-380" />

            {[{
            icon: <CalendarIcon data-id="element-381" />,
            title: '1. Book a Free Consultation Call',
            desc: "Your first consultation call is completely free. This is where we talk about what you're going through, what kind of support you need, and whether we're a good fit to work together. If you decide to continue after that, paid coaching options are available at the rates listed above."
          }, {
            icon: <MessageCircleIcon data-id="element-382" />,
            title: '2. Get Real',
            desc: 'We map out exactly where you are and where you want to be.'
          }, {
            icon: <SparklesIcon data-id="element-383" />,
            title: '3. Transform',
            desc: 'We do the work. You stop surviving and start living like a badass.'
          }].map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2
          }} className="relative z-10" data-id="element-384">
                <div className="w-24 h-24 mx-auto bg-white rounded-full shadow-sm border border-blush-100 flex items-center justify-center text-cta-500 mb-6" data-id="element-385">
                  {cloneElement(step.icon as React.ReactElement, {
                className: 'w-10 h-10'
              })}
                </div>
                <h3 className="font-heading text-2xl font-bold text-plum-700 mb-3" data-id="element-386">
                  {step.title}
                </h3>
                <p className="text-plum-500 font-medium" data-id="element-387">{step.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-4 bg-white relative overflow-hidden" data-id="element-388">
        <SparkleEffect count={50} data-id="element-389" />
        <div className="max-w-6xl mx-auto relative z-10" data-id="element-390">
          <div className="text-center mb-16" data-id="element-391">
            <h2 className="font-heading text-4xl font-bold text-plum-700 mb-4" data-id="element-392">
              Accessible pricing because everyone deserves support.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch" data-id="element-393">
            {/* Card 1 */}
            <div className="bg-white border border-blush-100 rounded-[3rem] p-10 shadow-xl flex flex-col relative overflow-hidden" data-id="element-394">
              <h3 className="font-heading text-2xl font-bold text-plum-700 mb-2" data-id="element-395">
                1:1 Session
              </h3>
              <p className="text-plum-500 font-medium mb-6" data-id="element-396">45–60 minutes</p>
              <div className="mb-8" data-id="element-397">
                <span className="font-heading text-4xl font-bold text-plum-700" data-id="element-398">
                  $25–$45
                </span>
              </div>
              <p className="text-plum-600 font-medium mb-10 flex-grow" data-id="element-399">
                A single deep-dive session to tackle what's on your mind right
                now.
              </p>
              <a href="https://calendly.com/trippinwithmandy/free-consultation-call" target="_blank" rel="noopener noreferrer" className="w-full py-4 rounded-full bg-plum-700 text-white font-bold text-lg hover:bg-plum-800 transition-colors text-center block" data-id="element-400">
                Book Free Consultation
              </a>
            </div>

            {/* Card 2 (Most Popular) */}
            <div className="bg-gradient-to-br from-plum-900 to-plum-800 rounded-[3rem] p-10 shadow-2xl flex flex-col relative overflow-hidden transform md:-translate-y-4" data-id="element-401">
              <div className="absolute top-0 right-0 w-32 h-32 bg-cta-500/20 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" data-id="element-402" />
              <div className="inline-flex self-start px-4 py-1.5 rounded-full bg-cta-500 text-white text-xs font-bold uppercase tracking-wider mb-6" data-id="element-403">
                Most Popular
              </div>
              <h3 className="font-heading text-2xl font-bold text-white mb-2" data-id="element-404">
                3 Session Package
              </h3>
              <div className="mb-8 mt-6" data-id="element-405">
                <span className="font-heading text-4xl font-bold text-white" data-id="element-406">
                  $60–$100
                </span>
              </div>
              <p className="text-plum-100 font-medium mb-10 flex-grow" data-id="element-407">
                Three sessions to build momentum and create real, lasting
                change.
              </p>
              <a href="https://calendly.com/trippinwithmandy/free-consultation-call" target="_blank" rel="noopener noreferrer" className="w-full py-4 rounded-full bg-cta-500 text-white font-bold text-lg hover:bg-cta-600 transition-colors shadow-lg shadow-cta-500/30 text-center block" data-id="element-408">
                Book Free Consultation
              </a>
            </div>

            {/* Card 3 */}
            <div className="bg-white border border-blush-100 rounded-[3rem] p-10 shadow-xl flex flex-col relative overflow-hidden" data-id="element-409">
              <h3 className="font-heading text-2xl font-bold text-plum-700 mb-2" data-id="element-410">
                Monthly Support
              </h3>
              <div className="mb-8 mt-12" data-id="element-411">
                <span className="font-heading text-4xl font-bold text-plum-700" data-id="element-412">
                  $120–$180
                </span>
                <span className="text-plum-500 font-medium" data-id="element-413">/month</span>
              </div>
              <p className="text-plum-600 font-medium mb-10 flex-grow" data-id="element-414">
                Ongoing support with weekly check-ins to keep you on track and
                thriving.
              </p>
              <a href="https://calendly.com/trippinwithmandy/free-consultation-call" target="_blank" rel="noopener noreferrer" className="w-full py-4 rounded-full bg-plum-700 text-white font-bold text-lg hover:bg-plum-800 transition-colors text-center block" data-id="element-415">
                Book Free Consultation
              </a>
            </div>
          </div>

          <p className="text-center text-plum-500 font-medium mt-10 max-w-2xl mx-auto" data-id="element-416">
            Your first consultation call is free. You'll receive a Google Meet
            link after booking.
            <br data-id="element-417" />
            <br data-id="element-418" />
            All sessions are personalized to your needs. Once you submit your
            request, I'll reach out to schedule and confirm details.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 text-center px-4" data-id="element-419">
        <div className="max-w-2xl mx-auto bg-white p-12 rounded-[3rem] shadow-xl border border-blush-100 relative overflow-hidden" data-id="element-420">
          <SparkleEffect count={50} data-id="element-421" />
          <div className="relative z-10" data-id="element-422">
            <h2 className="font-heading text-3xl font-bold text-plum-700 mb-4" data-id="element-423">
              Ready to invest in yourself?
            </h2>
            <p className="text-plum-500 mb-8 font-medium" data-id="element-424">
              Real-life guidance, mindset support, and confidence rebuilding.
              Let's figure out the right fit for you.
            </p>
            <a href="https://calendly.com/trippinwithmandy/free-consultation-call" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-cta-500 text-white font-bold text-lg hover:bg-cta-600 transition-all shadow-lg hover:-translate-y-1" data-id="element-425">
              <GlitterHeart size={20} variant="light" data-id="element-426" />
              Book Free Consultation
            </a>
          </div>
        </div>
      </section>
    </div>;
}